import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Tooltip,
  Stack,
  Typography,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { styled } from "@mui/material/styles";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import { UserLoginInfo } from "../../types/context";
import { appLogger, Http } from "../../utils";
import { UserContext } from "../../context/userContext";
import {
  IProductParams,
  ROLE_USER_ADMIN,
  ROLE_USER_SUPERADMIN,
  FEEDBACK_TYPE_FEEDBACK,
  FEEDBACK_FLOW_STATIC,
  PLATFORM_TYPE_MOBILE,
  PLATFORM_TYPE_BROWSER,
  IPlatformType,
  webTechs,
  mobTechs,
  tvTechs,
  PLATFORM_TYPE_TV,
  FEEDBACK_TYPE_BUGS,
  FEEDBACK_FLOW_DYNAMIC,
  FEEDBACK_TYPE_FEATURE_REQ,
  PlatformTypesList,
  TYPE_NONE,
  TYPE_IMAGE,
} from "../../model";
import { withSnackbar } from "../../utils/common/SnackbarHoc";
import DOMPurify from "dompurify";

function BasicDetails(props: any) {
  appLogger.info({
    infoObj: {
      method: "BasicDetails",
      component: "add-product",
      fileName: "basicDetails.tsx",
      props: props,
    },
  });
  const {
    productParams,
    setProductParams,
    errMsg,
    setErrMsg,
    basicSettingsMandatoryCheck,
  } = props;
  const [imageUrl, setImageUrl] = useState<any | null>(null);
  const [groupLists, setGroupLists] = React.useState([] as any);
  const [loading, setLoading] = useState(false);
  const [ProductConfigChangeAlert, setProductConfigChangeAlert] =
    useState(false);

  const [selectedTech, setTechnology] = React.useState(webTechs[0].value);

  const [deleteFileName, setDeleteFileName] = useState("");

  const { userInfo } = useContext(UserContext) as UserLoginInfo;

  const handleLogoChange = (url: string, dataInfo: any) => {
    if (productParams) {
      const temp: IProductParams | undefined = { ...productParams };
      if (temp && temp.products && temp.products[0]) {
        temp.products[0].logo = url;
        if (temp.products[0].feedbackAgentSettings)
          temp.products[0].feedbackAgentSettings.logoReq = dataInfo;

        setProductParams(temp);
      }
    }
  };

  const clearErrMsg = (errMsg: any) => {
    if (
      errMsg.productNameErr &&
      productParams &&
      productParams.products &&
      productParams.products[0] &&
      productParams.products[0].name
    ) {
      const msg = { ...errMsg };
      msg.productNameErr = null;
      setErrMsg(msg);
    }

    if (
      errMsg.productVersionErr &&
      productParams &&
      productParams.products &&
      productParams.products[0] &&
      productParams.products[0].version
    ) {
      const msg = { ...errMsg };
      msg.productVersionErr = null;
      setErrMsg(msg);
    }
    if (
      errMsg.productPlatformErr &&
      productParams &&
      productParams.products &&
      productParams.products[0] &&
      productParams.products[0].platform
    ) {
      const msg = { ...errMsg };
      msg.productPlatformErr = null;
      setErrMsg(msg);
    }
  };

  const selectPlatform = (event: SelectChangeEvent) => {
    if (productParams) {
      const temp: IProductParams | undefined = { ...productParams };
      if (temp && temp.products && temp.products[0]) {
        temp.products[0].platform = event.target.value as IPlatformType;
        if (temp.products[0] && temp.products[0].feedbackAgentSettings) {
          if (event.target.value === PLATFORM_TYPE_TV) {
            temp.products[0].feedbackAgentSettings.bannerImage = "";
            temp.products[0].feedbackAgentSettings.bannerText = "";
            temp.products[0].feedbackAgentSettings.bannerType = TYPE_NONE;
            delete temp.products[0].feedbackAgentSettings.bannerImageReq;

            temp.products[0].feedbackAgentSettings.feedbackTypes = [
              FEEDBACK_TYPE_FEEDBACK,
            ];
            if (
              temp.products[0].feedbackAgentSettings.feedbackSettings &&
              temp.products[0].feedbackAgentSettings.feedbackSettings.type
            ) {
              temp.products[0].feedbackAgentSettings.feedbackSettings.type =
                FEEDBACK_FLOW_DYNAMIC;
            }
          } else {
            if (event.target.value === PLATFORM_TYPE_MOBILE) {
              if (
                temp.products[0].feedbackAgentSettings.bannerType == TYPE_IMAGE
              ) {
                temp.products[0].feedbackAgentSettings.bannerImage = "";
                temp.products[0].feedbackAgentSettings.bannerText = "";
                temp.products[0].feedbackAgentSettings.bannerType = TYPE_NONE;
                delete temp.products[0].feedbackAgentSettings?.bannerImageReq;
              }
            }
            temp.products[0].feedbackAgentSettings.feedbackTypes = [
              FEEDBACK_TYPE_FEEDBACK,
              FEEDBACK_TYPE_BUGS,
              FEEDBACK_TYPE_FEATURE_REQ,
            ];
            if (
              temp.products[0].feedbackAgentSettings.feedbackSettings &&
              temp.products[0].feedbackAgentSettings.feedbackSettings.type
            ) {
              temp.products[0].feedbackAgentSettings.feedbackSettings.type =
                FEEDBACK_FLOW_STATIC;
            }
          }
        }
        setProductParams(temp);
        clearErrMsg(errMsg);
      }
    }
  };

  const selectProductName = (e: any) => {
    if (productParams) {
      const temp: IProductParams | undefined = { ...productParams };
      if (temp && temp.products && temp.products[0]) {
        temp.products[0].name = e.target.value;
        if (e && e.target && e.target.value) {
          temp.products[0].searchKey = e.target.value.toLowerCase().trim();
        }
        setProductParams(temp);
        clearErrMsg(errMsg);
        if (temp.products[0].id != "") {
          setProductConfigChangeAlert(true);
        }
      }
    }
  };

  const deleteLogo = (logo: string) => {
    setLoading(true);
    if (
      productParams &&
      productParams.products &&
      productParams.products[0] &&
      productParams.products[0].id &&
      productParams.products[0].id != ""
    ) {
      const filenames: string[] = logo.split("/");
      const filename = filenames[filenames.length - 1];
      const dataInfo = {
        deleteFileName: filename,
      };
      setDeleteFileName(filename);
      handleLogoChange("", dataInfo);
    } else {
      handleLogoChange("", undefined);
    }
  };

  const fetchGroup = () => {
    if (
      userInfo &&
      userInfo.userDetails &&
      (userInfo.userDetails.userRole === ROLE_USER_ADMIN ||
        userInfo.userDetails.userRole === ROLE_USER_SUPERADMIN)
    ) {
      Http.get({
        url: `/api/v2/groups/0`,
        state: userInfo,
      })
        .then((response: any) => {
          if (response && response.groupList && response.groupList.length > 0) {
            setGroupLists(response.groupList);
            appLogger.info({
              infoObj: {
                method: "fetchGroup",
                component: "add-product",
                fileName: "basicDetails.tsx",
                groupResponse: response,
              },
            });
          } else {
          }
        })
        .catch((error: any) => {
          appLogger.error({
            errObj: {
              method: "fetchGroup",
              component: "add-product",
              fileName: "basicDetails.tsx",
              err: error,
            },
          });
          props.snackbarShowMessage(error.message, "error");
        });
    }
  };

  useEffect(() => {
    fetchGroup();
  }, []);

  const handleChangeEvent = (event: any) => {
    event.target.value = null;
  };

  const selectProductVersion = (e: any) => {
    if (productParams) {
      const temp: IProductParams | undefined = { ...productParams };
      if (temp && temp.products && temp.products[0]) {
        temp.products[0].version = e.target.value;
        setProductParams(temp);
        clearErrMsg(errMsg);
        if (temp.products[0].id != "") {
          setProductConfigChangeAlert(true);
        }
      }
    }
  };

  function formatFileSize(bytes: number, decimalPoint: number) {
    if (bytes == 0) return "0 Bytes";
    const k = 1000,
      dm = decimalPoint || 2,
      sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"],
      i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  }

  const handleChange = (event: any) => {
    const file = event.target.files[0];
    const formUpload = new FormData();

    const size = file.size;
    const cur = formatFileSize(size, 2);
    if (!cur.includes("Bytes") && !cur.includes("KB")) {
      props.snackbarShowMessage(
        "You must give an image with less then 1MB",
        "error",
        3000
      );
    } else {
      formUpload.append("file", file);
      formUpload.append("fileName", file.name);
      setLoading(true);
      const reader = new FileReader();
      reader.onload = function () {
        const [fileType, base64String] = String(reader.result).split("base64,");
        if (!fileType.includes("image")) {
          props.snackbarShowMessage("Invalid image!", "error", 3000);
          return;
        }

        let sanitizedBase64String;
        let sanitizedFile;
        if (fileType.includes("svg")) {
          const sanitizedString = DOMPurify.sanitize(atob(base64String));
          sanitizedBase64String = btoa(sanitizedString);
          sanitizedFile = new Blob([sanitizedString], {
            type: "image/svg+xml",
          });
        } else {
          sanitizedBase64String = base64String;
          sanitizedFile = file;
        }

        const extension = file.name.split(".")[1];
        const dataInfo = {
          file: sanitizedBase64String,
          fileName: `logo.${extension}`,
          deleteFileName: "",
        };
        if (deleteFileName && deleteFileName != "") {
          dataInfo.deleteFileName = deleteFileName;
        }
        const resultString = URL.createObjectURL(sanitizedFile);
        handleLogoChange(resultString, dataInfo);
      };
      reader.onerror = function (event) {
        appLogger.error(
          {
            errObj: {
              method: "handleChange",
              component: "add-product",
              fileName: "basicDetails.tsx",
              event: event,
            },
          },
          "Error uploading img file"
        );
        reader.abort();
      };
      reader.readAsDataURL(file);
    }
  };

  const getDesc = (e: any) => {
    if (productParams) {
      const temp: IProductParams | undefined = { ...productParams };
      if (temp && temp.products && temp.products[0]) {
        temp.products[0].description = e.target.value;
        setProductParams(temp);
      }
    }
  };

  let techDDdatas: any = [];
  if (
    productParams &&
    productParams.products &&
    productParams.products[0] &&
    productParams.products[0].platform &&
    productParams.products[0].platform === PLATFORM_TYPE_MOBILE
  ) {
    techDDdatas = mobTechs;
  } else if (
    productParams &&
    productParams.products &&
    productParams.products[0] &&
    productParams.products[0].platform &&
    productParams.products[0].platform === PLATFORM_TYPE_TV
  ) {
    techDDdatas = tvTechs;
  } else {
    techDDdatas = webTechs;
  }

  const handleTechnologyChange = (event: any) => {
    setTechnology(event.target.value);
    if (productParams) {
      const temp: IProductParams | undefined = { ...productParams };
      if (temp && temp.products && temp.products[0]) {
        temp.products[0].technology = event.target.value;
        setProductParams(temp);
      }
    }
  };

  const [svgContent, setSvgContent] = useState<any>(null);

  useEffect(() => {
    const fetchSvg = async () => {
      const imgPath =
        productParams &&
        productParams.products &&
        productParams.products[0] &&
        productParams.products[0].logo
          ? productParams.products[0].logo
          : "";
      // "https://dashboard-cuvo-upload-photos.s3.amazonaws.com/product_6d414070-8396-11ee-a0ed-f3e6d67f790f/Demo1/logo.png";
      const fileExtension = getFileExtension(imgPath);
      if (fileExtension != "svg") {
        setSvgContent(
          productParams &&
            productParams.products &&
            productParams.products[0] &&
            productParams.products[0].logo
            ? productParams.products[0].logo
            : ""
        );
      } else {
        try {
          const response = await fetch(
            productParams &&
              productParams.products &&
              productParams.products[0] &&
              productParams.products[0].logo
              ? productParams.products[0].logo
              : ""
          );

          if (!response.ok) {
            throw new Error(`Failed to fetch SVG. Status: ${response.status}`);
          }
          const svgText = await response.text();
          setSvgContent(`data:image/svg+xml;base64,${btoa(svgText)}`);
        } catch (error) {
          // console.error("Error fetching SVG:", error);
        }
      }
    };

    fetchSvg();
  }, [productParams]);
  const getFileExtension = (url: any) => {
    const parts = url.split(".");
    if (parts.length > 1) {
      return parts[parts.length - 1];
      // eslint-disable-next-line @typescript-eslint/tslint/config
    } else {
      return null;
    }
  };
  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={24}>
          <Typography className="title">Basic Details</Typography>
        </Grid>
        {ProductConfigChangeAlert ? (
          <Grid item xs={12} md={12}>
            <Stack sx={{ width: "100%" }} className="stack-prod-alert">
              <Alert severity="info">
                <Typography>
                  <strong>i).Changing Accounts's Name </strong>
                  will be updated for all the <b>"Versions"</b> of this product.
                  <br />
                </Typography>
                <Typography>
                  <strong>ii).Changing Version </strong>
                  will be created as a <b>"New Version"</b> of this product.{" "}
                </Typography>
              </Alert>
            </Stack>
          </Grid>
        ) : (
          <></>
        )}

        <Grid item xs={12} md={6}>
          <FormControl className="custom-select guideTourproductName" fullWidth>
            <InputLabel>
              Account name
              <div style={{ display: "inline-block", color: "red" }}>*</div>
              {errMsg.productNameErr ? (
                <div style={{ display: "inline-block", color: "red" }}>
                  {errMsg.productNameErr}
                </div>
              ) : (
                <></>
              )}
            </InputLabel>

            <OutlinedInput
              type="text"
              value={
                productParams &&
                productParams.products &&
                productParams.products[0] &&
                productParams.products[0].name
                  ? productParams.products[0].name
                  : ""
              }
              onChange={selectProductName}
              disabled={
                productParams &&
                productParams.products &&
                productParams.products[0] &&
                productParams.products[0].id &&
                productParams.products[0].cloneNewVersion
              }
              id="product-select"
              label="Product name"
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl
            className="custom-select guideTourproductVersion"
            fullWidth
          >
            <InputLabel>
              Version{" "}
              <div style={{ display: "inline-block", color: "red" }}>*</div>{" "}
              {errMsg.productVersionErr ? (
                <div style={{ display: "inline-block", color: "red" }}>
                  {errMsg.productVersionErr}
                </div>
              ) : (
                <></>
              )}
            </InputLabel>
            <OutlinedInput
              type="text"
              value={
                productParams &&
                productParams.products &&
                productParams.products[0] &&
                productParams.products[0].version
                  ? productParams.products[0].version
                  : ""
              }
              onChange={selectProductVersion}
              id="product-version"
              disabled={
                productParams &&
                productParams.products &&
                productParams.products[0] &&
                productParams.products[0].id &&
                !(
                  productParams &&
                  productParams.products &&
                  productParams.products[0] &&
                  productParams.products[0].cloneNewVersion
                )
              }
              label="Product version"
            />
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <FormControl className="custom-select custom-textarea" fullWidth>
            <InputLabel>Description</InputLabel>
            <OutlinedInput
              type="text"
              value={
                productParams &&
                productParams.products &&
                productParams.products[0] &&
                productParams.products[0].description
                  ? productParams.products[0].description
                  : ""
              }
              onChange={getDesc}
              id="desc-input"
              label="Description"
              multiline
              maxRows={4}
            />
          </FormControl>
        </Grid>
      </Grid>

      <Box sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <Typography className="title">
            logo :
            <span className="title-tooltip">
              (Image size must be less than 1 MB.) (Maximum image dimension
              should be 120 x 80)
            </span>
          </Typography>

          <Box
            className={loading ? "upload-wrap" : "upload-wrap"}
            sx={{ display: "flex" }}
          >
            {!(
              productParams &&
              productParams.products &&
              productParams.products[0] &&
              productParams.products[0] &&
              productParams.products[0].logo
            ) && (
              <Button
                variant="outlined"
                component="label"
                className="upload-btn"
              >
                <i className="icon-upload" />
                upload Logo Image
                <input
                  hidden
                  accept="image/*"
                  onClick={(event) => handleChangeEvent(event)}
                  onChange={(e) => handleChange(e)}
                  type="file"
                />
              </Button>
            )}

            {productParams &&
              productParams.products &&
              productParams.products[0] &&
              productParams.products[0] &&
              productParams.products[0].logo && (
                <Box className="img-preview">
                  <img id="svgImage" src={svgContent} />
                  {/* <img
                    src={
                      "data:image/svg+xml;charset=utf-8," + productParams &&
                      productParams.products &&
                      productParams.products[0] &&
                      productParams.products[0] &&
                      productParams.products[0].logo
                        ? productParams.products[0].logo
                        : ""
                    }
                  /> */}
                </Box>
              )}

            {productParams &&
              productParams.products &&
              productParams.products[0] &&
              productParams.products[0] &&
              productParams.products[0].logo && (
                <Tooltip title="Delete Logo" placement="top">
                  <i
                    className="icon-delete cursor-pointer"
                    onClick={() => deleteLogo(productParams.products[0].logo)}
                  />
                </Tooltip>
              )}
          </Box>
        </Grid>
      </Box>
    </Box>
  );
}
export default withSnackbar(BasicDetails);
